import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hero badge: surfaces the global HD-audiobook progress as it rolls in.
 *
 * - Polls /api/book/translations every 60s (component is mounted on the
 *   landing page, so cheap re-fetches keep the badge live without a page
 *   reload).
 * - Hidden entirely until at least the English edition has its full set
 *   of HD chapters in object storage (audio_count >= chapter_count).
 * - Tag line auto-grows: "56 HD chapters" alone → "in 2 languages" →
 *   "in 12 languages" as more translations finish their TTS run.
 */
export default function HdAudiobookBadge() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${API}/book/translations`)
        .then((r) => r.json())
        .then((d) => alive && setData(d))
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (!data) return null;
  const translations = data.translations || [];
  const en = translations.find((t) => t.code === "en");
  if (!en || !en.chapter_count) return null;

  const enComplete = en.audio_count >= en.chapter_count;
  if (!enComplete) return null; // hidden until English HD is fully done

  const fullAudioLangs = translations.filter(
    (t) => t.chapter_count > 0 && t.audio_count >= t.chapter_count
  ).length;
  // Total languages the book exists in (text translation done). The
  // audiobook is still rolling out language by language, so we surface
  // *availability* — not just audio-complete count — so visitors who
  // came to read (or listen in the language that *is* ready) get the
  // right expectation.
  const textLangs = translations.filter((t) => t.chapter_count > 0).length;

  const langLabel =
    textLangs > 1 ? `in ${textLangs} languages` : "English HD edition";

  // One-tap share: Web Share on mobile (X/WhatsApp/etc native sheet),
  // Twitter intent fallback on desktop. Pre-written copy is the
  // milestone — visitors share with zero typing.
  const SITE_URL = "https://twicebuilttwicebroken.co.uk";
  const shareTitle = "Twice Built, Twice Broken";
  const shareText =
    textLangs > 1
      ? `Just discovered "Twice Built, Twice Broken" — an unflinching memoir by Aysin Djemil, free to read in ${textLangs} languages with HD audio in ${fullAudioLangs}.`
      : `Just discovered "Twice Built, Twice Broken" — an unflinching memoir by Aysin Djemil, now free as a full ${en.chapter_count}-chapter HD audiobook.`;
  const tweetIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText + " "
  )}&url=${encodeURIComponent(SITE_URL)}`;

  const onShare = async (e) => {
    if (typeof navigator !== "undefined" && navigator.share) {
      e.preventDefault();
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: SITE_URL,
        });
      } catch {
        /* user cancelled or share unsupported — let link fallback */
      }
    }
    // otherwise: anchor's href opens Twitter intent in a new tab
  };

  return (
    <div className="hd-badge-row" data-testid="hd-badge-row">
      <div
        className="hd-audiobook-badge"
        data-testid="hd-audiobook-badge"
        role="status"
        aria-label={`${en.chapter_count} HD audiobook chapters available, ${langLabel}`}
      >
        <span className="hd-badge-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M12 1a9 9 0 00-9 9v7a3 3 0 003 3h2v-8H5v-2a7 7 0 0114 0v2h-3v8h2a3 3 0 003-3v-7a9 9 0 00-9-9z" />
          </svg>
        </span>
        <span className="hd-badge-text">
          <strong>{en.chapter_count} HD chapters</strong>
          <span className="hd-badge-dot" aria-hidden="true">·</span>
          <span className="hd-badge-langs">{langLabel}</span>
        </span>
      </div>

      <a
        className="hd-badge-share"
        href={tweetIntent}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onShare}
        data-testid="hd-badge-share"
        aria-label="Share this milestone"
      >
        <span className="hd-share-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M18 16a3 3 0 00-2.36 1.14L8.9 13.36a3 3 0 000-2.72l6.74-3.78A3 3 0 1015 5a3 3 0 00.06.58L8.32 9.36a3 3 0 100 5.28l6.74 3.78A3 3 0 1018 16z" />
          </svg>
        </span>
        <span>Share this</span>
      </a>
    </div>
  );
}
